import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { Card, Overline, StatCard, ProgressBar, Pill, Icon, PageLoader, SectionTitle } from "../../components/ui-kit";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

export default function AdminDashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/dashboard").then(({ data }) => setD(data)); }, []);
  if (!d) return <PageLoader />;

  const maxCost = Math.max(...d.feature_breakdown.map((f) => f.cost), 0.0001);

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-6">
      <div className="relative rounded-[28px] p-6 sm:p-7 text-white overflow-hidden" style={{ background: "linear-gradient(135deg,#8a6df0,#5f3fd0)" }}>
        <div className="absolute inset-0 dotgrid opacity-50" />
        <Icon name="ShieldCheck" className="absolute -right-4 -bottom-5 w-36 h-36 opacity-15" />
        <div className="relative"><Overline className="text-white/80">Platform Control Centre</Overline><h1 className="text-3xl sm:text-4xl font-bold mt-1 tracking-tight">Super Admin</h1></div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon="Building2" label="Schools" value={d.total_schools} tone="blue" />
        <StatCard icon="Presentation" label="Teachers" value={d.total_teachers} tone="green" />
        <StatCard icon="GraduationCap" label="Students" value={d.total_students} tone="cyan" />
        <StatCard icon="Users" label="Active Users" value={d.active_users} tone="yellow" />
      </div>

      <SectionTitle title="AI Usage & Cost Monitoring" icon="Cpu" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon="Sparkles" label="AI Generations" value={d.ai_generations} tone="purple" />
        <StatCard icon="DollarSign" label="Cost Today" value={`$${d.ai_cost_today}`} tone="green" sub={`$${d.ai_cost_month} this month`} />
        <StatCard icon="Database" label="Cache Hit Rate" value={`${d.cache_hit_rate}%`} tone="cyan" sub="reused content" />
        <StatCard icon="TriangleAlert" label="Failed Gens" value={d.failed_generations} tone="coral" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <SectionTitle title="AI Cost Trend" icon="TrendingUp" />
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={d.usage_trend}>
              <defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#7657E8" stopOpacity={0.3} /><stop offset="100%" stopColor="#7657E8" stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E8EEF6" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#71809B" }} tickFormatter={(v) => v.slice(5)} />
              <YAxis tick={{ fontSize: 10, fill: "#71809B" }} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #DCE5F0" }} />
              <Area type="monotone" dataKey="cost" stroke="#7657E8" strokeWidth={2.5} fill="url(#g)" />
            </AreaChart>
          </ResponsiveContainer>
        </Card>
        <Card>
          <SectionTitle title="Cost by Feature" icon="PieChart" action={<Pill tone="purple">Most: {d.most_expensive_feature}</Pill>} />
          <div className="space-y-3">
            {d.feature_breakdown.map((f) => (
              <div key={f.feature}>
                <div className="flex justify-between text-sm mb-1"><span className="font-medium text-mim-navy capitalize">{f.feature.replace(/_/g, " ")}</span><span className="text-mim-muted">${f.cost.toFixed(4)} · {f.count}×</span></div>
                <ProgressBar value={(f.cost / maxCost) * 100} color="#7657E8" height="h-2" />
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <StatCard icon="FileStack" label="Content Items" value={d.content_count} tone="blue" />
        <StatCard icon="CircleHelp" label="Questions" value={d.questions_count} tone="green" />
        <StatCard icon="PlaySquare" label="Videos" value={d.videos_count} tone="coral" />
      </div>

      <Card>
        <SectionTitle title="Recent AI Generations" icon="History" />
        <div className="divide-y divide-mim-line">
          {d.recent_generations.map((g) => (
            <div key={g.id} className="flex items-center gap-3 py-2.5 text-sm">
              <Pill tone={g.success ? "green" : "coral"}>{g.success ? "success" : "failed"}</Pill>
              <span className="capitalize font-medium text-mim-navy flex-1">{g.feature.replace(/_/g, " ")}</span>
              {g.cached && <Pill tone="purple">cached</Pill>}
              <span className="text-xs text-mim-muted">${(g.cost || 0).toFixed(4)}</span>
              <span className="text-xs text-mim-muted hidden sm:block">{new Date(g.created_at).toLocaleDateString()}</span>
            </div>
          ))}
        </div>
      </Card>

      <Card className="bg-mim-soft border-mim-blue/15 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3"><Icon name="Settings" className="w-5 h-5 text-mim-blue" /><span className="font-semibold text-mim-navy">Trial period: {d.subscriptions.trial_days} days (configurable) · {d.subscriptions.school} school plan active</span></div>
        <Link to="/teacher/curriculum" className="text-sm font-semibold text-mim-blue hover:underline">AI Content Factory →</Link>
      </Card>
    </div>
  );
}
